import { createContext, useContext, type ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "convex/react";
import { anyApi } from "convex/server";
import type { PublicPropertyContext } from "../../shared/types";

const PropertyCtx = createContext<PublicPropertyContext | null>(null);

type PropertyRow = {
  slug: string;
  name: string;
  address: string;
  timezone: string;
};

// Resolves the route's :propertySlug param against Convex. While
// loading, context stays null (children render skeletons). On a slug
// that doesn't match any property, redirects to /. On a hit, provides
// the property to children via useProperty().
export function PropertyProvider({ children }: { children: ReactNode }) {
  const { propertySlug } = useParams<{ propertySlug: string }>();
  const row = useQuery(
    anyApi.properties.getBySlug,
    propertySlug ? { slug: propertySlug } : "skip",
  ) as PropertyRow | null | undefined;

  if (row === null) {
    return <Navigate to="/" replace />;
  }

  const value: PublicPropertyContext | null = row
    ? { slug: row.slug, name: row.name, address: row.address }
    : null;

  return <PropertyCtx.Provider value={value}>{children}</PropertyCtx.Provider>;
}

export function useProperty(): PublicPropertyContext | null {
  return useContext(PropertyCtx);
}
